import React, { useState, useEffect } from "react";
import { Search, Calendar, ShieldAlert } from "lucide-react";
import { get_suspect_report } from "../../services/api-service";
import { REACT_APP_API_URL, API_ROUTES } from "../../config/api-config";
import { toast } from "react-toastify";

// ============================================================
// COMPONENT: AsyncImage
// ============================================================
function AsyncImage({ src, alt, style, onClick }) {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div
      onClick={onClick}
      style={{
        position: "relative",
        width: style.width || "100%",
        maxWidth: style.maxWidth || "130px",
        height: style.height || "130px",
        display: "inline-block",
        cursor: onClick ? "pointer" : "default"
      }}
    >
      {!hasLoaded && !error && (
        <div
          style={{
            position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
            borderRadius: style.borderRadius || "6px", backgroundColor: "#f3f4f6",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#6b7280", fontSize: "11px", fontWeight: "500", border: "1px dashed #d1d5db", zIndex: 1,
          }}
        >
          ⏳
        </div>
      )}
      {error && (
        <div
          style={{
            position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
            borderRadius: style.borderRadius || "6px", backgroundColor: "#fee2e2",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#ef4444", fontSize: "11px", fontWeight: "500", border: "1px solid #fca5a5", zIndex: 1,
          }}
        >
          ⚠️
        </div>
      )}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        style={{
          ...style,
          opacity: hasLoaded ? 1 : 0,
          transition: "opacity 0.2s ease-in-out",
        }}
        onLoad={() => setHasLoaded(true)}
        onError={() => setError(true)}
      />
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function SuspectsReportTable() {
  const [logs, setLogs] = useState([]);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split("T")[0]);
  const [searchName, setSearchName] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState(null);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchLogs();
  }, [filterDate]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await get_suspect_report({ date: filterDate, suspect_name: searchName });
      setLogs(res.data.data);
      setCurrentPage(1); // Reset to first page on new search
    } catch (err) {
      toast.error("Failed to load suspect records");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchLogs();
  };

  // Pagination Logic
  const totalPages = Math.ceil(logs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentLogs = logs.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div style={{ backgroundColor: "#ffffff", padding: "24px", borderRadius: "16px", border: "1px solid #e2e8f0" }}>
      
      {/* FILTER CONTROLS BAR */}
      <form onSubmit={handleSearchSubmit} style={{ display: "flex", gap: "15px", flexWrap: "wrap", marginBottom: "25px", alignItems: "flex-end" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label style={{ fontSize: "12px", color: "#64748b", fontWeight: "600" }}>Log Date</label>
          <div style={{ position: "relative" }}>
            <Calendar size={16} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#64748b" }} />
            <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} style={{ padding: "10px 12px 10px 36px", borderRadius: "8px", border: "1px solid #cbd5e1", color: "#334155" }} />
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "6px", flexGrow: 1, maxWidth: "350px" }}>
          <label style={{ fontSize: "12px", color: "#64748b", fontWeight: "600" }}>Suspect Name / ID</label>
          <div style={{ position: "relative" }}>
            <Search size={16} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#64748b" }} />
            <input type="text" placeholder="Search parameters..." value={searchName} onChange={(e) => setSearchName(e.target.value)} style={{ width: "100%", padding: "10px 12px 10px 36px", borderRadius: "8px", border: "1px solid #cbd5e1" }} />
          </div>
        </div>

        <button type="submit" style={{ padding: "11px 24px", backgroundColor: "#dc2626", color: "white", border: "none", borderRadius: "8px", fontWeight: "600", cursor: "pointer" }}>Query Records</button>
      </form>

      {/* COMPONENT TABLE FRAME */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>Querying watchlogs...</div>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #f1f5f9", borderRadius: "8px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "700px" }}>
            <thead style={{ backgroundColor: "#f8fafc" }}>
              <tr style={{ borderBottom: "2px solid #e2e8f0", textAlign: "left" }}>
                <th style={{ padding: "14px 16px", color: "#475569", fontWeight: "600", fontSize: "13px" }}>Captured Face</th>
                <th style={{ padding: "14px 16px", color: "#475569", fontWeight: "600", fontSize: "13px" }}>Timestamp</th>
                <th style={{ padding: "14px 16px", color: "#475569", fontWeight: "600", fontSize: "13px" }}>Suspect Info</th>
                <th style={{ padding: "14px 16px", color: "#475569", fontWeight: "600", fontSize: "13px" }}>Detection Spot</th>
                <th style={{ padding: "14px 16px", color: "#475569", fontWeight: "600", fontSize: "13px" }}>Confidence</th>
              </tr>
            </thead>
            <tbody>
              {currentLogs.length > 0 ? (
                currentLogs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: "1px solid #f1f5f9", backgroundColor: "#fff" }}>
                    <td style={{ padding: "12px 16px" }}>
                      {log.has_image ? (
                        <AsyncImage
                          src={`${REACT_APP_API_URL}/${API_ROUTES.get_suspect_log_image(log.id)}`}
                          alt="Suspect Crop"
                          onClick={() => setSelectedImageId(log.id)}
                          style={{ width: "54px", height: "54px", borderRadius: "8px", objectFit: "cover", border: "2px solid #dc2626" }}
                        />
                      ) : (
                        <div style={{ width: "54px", height: "54px", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "8px" }}><ShieldAlert size={18} color="#dc2626"/></div>
                      )}
                    </td>
                    <td style={{ padding: "12px 16px", color: "#334155", fontWeight: "500", fontSize: "14px" }}>{log.detection_time}</td>
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ fontWeight: "700", color: "#1e293b", fontSize: "14px" }}>{log.suspect_name}</div>
                      <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>{log.suspect_id}</div>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ fontWeight: "500", color: "#334155", fontSize: "14px" }}>{log.location}</div>
                      <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "2px" }}>Cam #{log.camera_number} ({log.camera_name})</div>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{ padding: "6px 10px", background: "#fef2f2", color: "#b91c1c", borderRadius: "6px", fontWeight: "600", fontSize: "12px" }}>{(log.confidence * 100).toFixed(1)}% Match</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>No suspects detected within this tracking scope.</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* PAGINATION CONTROLS */}
          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderTop: "1px solid #e2e8f0", backgroundColor: "#f8fafc" }}>
              <span style={{ fontSize: "13px", color: "#64748b", fontWeight: "500" }}>
                Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, logs.length)} of {logs.length} entries
              </span>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: "6px 14px", border: "1px solid #cbd5e1", borderRadius: "6px",
                    backgroundColor: currentPage === 1 ? "#f1f5f9" : "white",
                    color: currentPage === 1 ? "#94a3b8" : "#334155",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    fontSize: "13px", fontWeight: "600", transition: "all 0.2s"
                  }}
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: "6px 14px", border: "1px solid #cbd5e1", borderRadius: "6px",
                    backgroundColor: currentPage === totalPages ? "#f1f5f9" : "white",
                    color: currentPage === totalPages ? "#94a3b8" : "#334155",
                    cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                    fontSize: "13px", fontWeight: "600", transition: "all 0.2s"
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* FULL PREVIEW ENLARGEMENT MODAL */}
      {selectedImageId && (
        <div onClick={() => setSelectedImageId(null)} style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(15, 23, 42, 0.75)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <img 
            src={`${REACT_APP_API_URL}/${API_ROUTES.get_suspect_log_image(selectedImageId)}`} 
            alt="Crop Preview" 
            style={{ maxWidth: "80%", maxHeight: "80%", borderRadius: "12px", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)" }} 
          />
        </div>
      )}

    </div>
  );
}
